import { useQuery } from '@tanstack/react-query'
import { API } from 'aws-amplify'

import { QUERY_KEYS } from 'constants/constants'
import { getSystem } from '../../graphql/queries'
import { notifyBugsnag } from '../../helpers'
import { setFlags } from './flags-store'

const FIVE_MINUTES = 5 * 60 * 1000
// Notaus (status paused) und Rollout-Schritte sollen auch Tabs erreichen, die
// lange offen sind. Im Hintergrund pollt react-query nicht, der naechste Tick
// nach der Rueckkehr holt den Stand.
const TEN_MINUTES = 10 * 60 * 1000

let reportedFailure = false

// Letzter erfolgreich geladener Stand in diesem Seitenaufruf. Schlaegt ein
// Neuladen fehl, bleibt er stehen, statt dass ein kurzer Netzfehler alle Flags
// abschaltet (z. B. pricing_enforcement mitten in der Sitzung). Nur wenn
// schon das erste Laden fehlschlaegt, laeuft alles mit leeren Flags.
let lastLoaded = null

/**
 * System-Item FEATURE_FLAGS, per IAM lesbar mit und ohne Login (gleicher Weg
 * wie SYSTEM_STATUS in use-system-status.js), damit auch /funnel es bekommt.
 *
 * Bewusst NICHT Teil von useSystemQuery: selectSystemData wirft bei fehlenden
 * Items und haelt die App dann im Loader. Fehlt das Item, gilt das als
 * geladen mit leeren Flags, also Status quo. Ein Fehler kommt als
 * `{ ok: false }` zurueck, damit die queryFn den letzten Stand halten kann.
 *
 * Direkt ueber API.graphql statt ueber query(): der Wrapper meldet jeden
 * Fehler an Bugsnag, und diese Abfrage laeuft bei jedem Seitenaufruf aller
 * Nutzer. Hier geht hoechstens eine Meldung pro Seitenaufruf raus.
 *
 * @returns {Promise<{ ok: boolean, flags: object }>}
 */
const fetchFlags = async () => {
  try {
    const res = await API.graphql({
      query: getSystem,
      variables: { id: 'FEATURE_FLAGS' },
      authMode: 'AWS_IAM'
    })
    const raw = res?.data?.getSystem?.data
    if (!raw) return { ok: true, flags: {} }
    const data = typeof raw === 'string' ? JSON.parse(raw) : raw
    return { ok: true, flags: data?.flags || {} }
  } catch (err) {
    if (!reportedFailure) {
      reportedFailure = true
      notifyBugsnag(
        new Error(
          `getSystem FEATURE_FLAGS: ${err?.errors?.[0]?.message || err?.message || 'unknown'}`
        )
      )
    }
    return { ok: false, flags: {} }
  }
}

let prefetched = null

/**
 * Startet die Abfrage sofort nach Amplify.configure (index.js), parallel zu
 * System-Status, Cognito und dem Wiederherstellen des Query-Caches. Der
 * Funnel wartet hoechstens 1,5 s auf die Flags, jede gesparte Millisekunde
 * zaehlt also fuer die Zahl der Besucher, die im Test landen.
 *
 * @returns {Promise<object>} die geladenen Flags, bei Fehler `{}`
 */
export const prefetchFeatureFlags = () => {
  if (!prefetched) prefetched = fetchFlags()
  return prefetched.then(({ flags }) => flags)
}

/**
 * Ein Fehler beim ersten Laden ergibt `{}`, ein Fehler beim Neuladen den
 * zuletzt erfolgreich geladenen Stand. Dasselbe Objekt wie vorher, damit
 * react-query keine neuen Daten meldet und setFlags nichts neu rechnet.
 */
export const loadFeatureFlags = async () => {
  const pending = prefetched || fetchFlags()
  prefetched = null
  const { ok, flags } = await pending
  if (ok) lastLoaded = flags
  const next = ok ? flags : lastLoaded || {}
  setFlags(next)
  return next
}

export const useFeatureFlagsQuery = () =>
  useQuery({
    queryKey: QUERY_KEYS.featureFlags,
    queryFn: loadFeatureFlags,
    staleTime: FIVE_MINUTES,
    refetchInterval: TEN_MINUTES,
    refetchOnWindowFocus: false,
    retry: false
  })

// Nur fuer Tests
export const resetFeatureFlagsQueryForTests = () => {
  reportedFailure = false
  lastLoaded = null
  prefetched = null
}
